import { useTranslation } from 'react-i18next';
import { MasterName } from '@Utils';

import { Badge } from '@Components';
import { TrackedButton } from '@Components/ui-v2/tracked-button';

interface NewMasterSummaryProps {
  composed: MasterName;
  sourceLabel: string;
  fileName: string;
  onBack: () => void;
  onConfirm: () => void;
}

/**
 * The second step of creating a master: what is about to be made.
 *
 * It exists because the name is composed from parts rather than typed,
 * so the first time anyone sees the result is here. Nothing is corrected
 * for them — a type that already names its source reads back doubled,
 * and going back is how that gets fixed.
 */
const NewMasterSummary = ({ composed, sourceLabel, fileName, onBack, onConfirm }: NewMasterSummaryProps) => {
  const { t } = useTranslation();

  // The tag is rendered apart: it is the vocabulary access is granted
  // in, not a sentence, and a badge is how it reads everywhere else.
  const rows = [
    { label: t('Configuration.ConfirmName'), value: composed.displayName, strong: true },
    { label: t('Configuration.Connector'), value: sourceLabel, strong: false },
    { label: t('Configuration.ConfirmFile'), value: fileName, strong: false },
  ];

  return (
    <div className="w-full flex flex-col gap-4">
      <p className="text-sm text-gray-600">{t('Configuration.ConfirmMessage')}</p>

      <dl className="w-full rounded-lg border border-gray-200 divide-y divide-gray-200">
        {rows.map((row) => (
          <div key={row.label} className="flex justify-between gap-4 px-4 py-3">
            <dt className="text-sm text-gray-500">{row.label}</dt>
            <dd className={`text-sm text-gray-900 text-right break-all ${row.strong ? 'font-semibold' : ''}`}>
              {row.value}
            </dd>
          </div>
        ))}
        <div className="flex justify-between gap-4 px-4 py-3 items-center">
          <dt className="text-sm text-gray-500">{t('Configuration.ConfirmTag')}</dt>
          <dd className="flex flex-wrap gap-1 justify-end">
            {composed.tags.map((tag) => (
              <Badge key={tag} variant="brand" label={tag} />
            ))}
          </dd>
        </div>
      </dl>

      <div className="flex gap-3 items-center w-full justify-center">
        <TrackedButton
          variant="outline"
          buttonName="UPLOAD_CONFIGURATION_FILE_BACK"
          onClick={onBack}
          isSubmit={false}
          className="w-full bg-white hover:border-bg-gray-300 hover:bg-gray-50 cursor-pointer hover:shadow-sm">
          <span className="flex items-center justify-center">{t('Configuration.Back')}</span>
        </TrackedButton>
        <TrackedButton
          buttonName="UPLOAD_CONFIGURATION_FILE_SUBMIT"
          isSubmit={false}
          onClick={onConfirm}
          className="w-full">
          <span className="flex items-center justify-center">{t('Configuration.ConfirmCreate')}</span>
        </TrackedButton>
      </div>
    </div>
  );
};

export default NewMasterSummary;
